import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { LayoutDashboard, ClipboardList, Plus, PiggyBank, Settings as SettingsIcon, Coins, Cloud, Loader, Download, X, CloudOff, Menu, Sun, Moon } from 'lucide-react';
import { BudgetProvider, useBudget } from './context/BudgetContext';
import { ToastContainer, ThemeToggle, FileSaveChip, ExternalChangeBar, Modal } from './components/Common/Common';
import { ErrorBoundary } from './components/Common/ErrorBoundary';
import BottomNav from './components/MobileNav/BottomNav';
import MobileEntrySheet from './components/MobileEntry/MobileEntrySheet';
import { useIsPhone } from './hooks/useIsPhone';
import useSlidingPill from './hooks/useSlidingPill';
import { usePwaInstall } from './hooks/usePwaInstall';
import './styles/index.css';

// In test environments React.lazy + Suspense hides content from synchronous
// queries, so we fall back to eager imports. Lazy loading is only beneficial
// in the browser anyway (chunk splitting).
/* eslint-disable import/no-cycle */
const isTest = process.env.NODE_ENV === 'test';

const Dashboard     = isTest ? require('./components/Dashboard/Dashboard').default    : lazy(() => import('./components/Dashboard/Dashboard'));
const BudgetPlanner = isTest ? require('./components/BudgetPlanner/BudgetPlanner').default : lazy(() => import('./components/BudgetPlanner/BudgetPlanner'));
const Tracker       = isTest ? require('./components/Tracker/Tracker').default        : lazy(() => import('./components/Tracker/Tracker'));
const Savings       = isTest ? require('./components/Savings/Savings').default        : lazy(() => import('./components/Savings/Savings'));
const Currency      = isTest ? require('./components/Currency/Currency').default      : lazy(() => import('./components/Currency/Currency'));
const Settings      = isTest ? require('./components/Settings/Settings').default      : lazy(() => import('./components/Settings/Settings'));

/** Persistent Google Drive sync state, shown next to the theme toggle. */
export function DriveStatusChip({ on, connecting, syncing, needsReconnect, dirty, lastSync, onReconnect, onSave }) {
  if (!on && !needsReconnect) return null;

  if (needsReconnect) {
    return (
      <button
        type="button"
        className="drive-sync-indicator drive-status-warning"
        onClick={onReconnect}
        disabled={connecting}
        title="Google Drive session expired — click to reconnect"
      >
        {connecting ? <Loader size={13} className="spin" /> : <CloudOff size={13} />}
        <span>{connecting ? 'Connecting…' : 'Reconnect'}</span>
      </button>
    );
  }
  if (connecting || syncing) {
    return (
      <span className="drive-sync-indicator" title={connecting ? 'Connecting to Drive…' : 'Syncing to Drive…'}>
        <Loader size={13} className="spin" />
        <span>{connecting ? 'Connecting…' : 'Syncing…'}</span>
      </span>
    );
  }
  if (dirty) {
    return (
      <button
        type="button"
        className="drive-sync-indicator drive-status-warning"
        onClick={onSave}
        title="Local changes not yet on Drive — click to save now"
      >
        <CloudOff size={13} />
        <span>Unsynced</span>
      </button>
    );
  }
  return (
    <span
      className="drive-sync-indicator drive-status-synced"
      title={lastSync ? `Drive synced ${new Date(lastSync).toLocaleString()}` : 'Google Drive sync on'}
    >
      <Cloud size={13} />
      <span>Synced</span>
    </span>
  );
}

const TABS = [
  { id: 'dashboard',  label: 'Dashboard',  Icon: LayoutDashboard },
  { id: 'budget',     label: 'Budget',     Icon: ClipboardList },
  { id: 'tracker',    label: 'Tracker',    Icon: Plus },
  { id: 'savings',    label: 'Savings',    Icon: PiggyBank },
  { id: 'currencies', label: 'Currencies', Icon: Coins },
  { id: 'settings',   label: 'Settings',   Icon: SettingsIcon },
];

function AppInner() {
  const [tab, setTab] = useState('dashboard');
  const [navRef, navPill] = useSlidingPill('.nav-tab.active', tab);
  const [menuOpen, setMenuOpen] = useState(false);
  const isPhone = useIsPhone();

  // Shrink the phone header + bottom nav once the page is scrolled.
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    if (!isPhone) return undefined;
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isPhone]);

  // Swipable drawer: drag from the left edge to open, drag the open drawer left
  // to close. Pointer is only captured once a real horizontal move starts, so
  // taps on the burger / drawer items still fire normally.
  const DRAWER_W = 240;
  const [drawerDrag, setDrawerDrag] = useState(null); // px 0..DRAWER_W while dragging, else null
  const drawerGesture = useRef(null);
  const suppressDrawerClick = useRef(false);
  const drawerDragging = drawerDrag !== null;
  const drawerPos = drawerDragging ? drawerDrag : (menuOpen ? DRAWER_W : 0);

  const beginDrawer = (mode) => (e) => {
    drawerGesture.current = { id: e.pointerId, startX: e.clientX, mode, moved: false, pos: mode === 'open' ? 0 : DRAWER_W };
  };
  const moveDrawer = (e) => {
    const g = drawerGesture.current;
    if (!g) return;
    const dx = e.clientX - g.startX;
    if (!g.moved && Math.abs(dx) > 8) {
      g.moved = true;
      try { e.currentTarget.setPointerCapture(g.id); } catch { /* capture is best-effort */ }
    }
    if (!g.moved) return;
    const base = g.mode === 'open' ? 0 : DRAWER_W;
    g.pos = Math.max(0, Math.min(DRAWER_W, base + dx));
    setDrawerDrag(g.pos);
  };
  const endDrawer = () => {
    const g = drawerGesture.current;
    drawerGesture.current = null;
    if (g?.moved) {
      suppressDrawerClick.current = true; // swallow the click a drag would synthesize
      setMenuOpen(g.pos > DRAWER_W / 2);
    }
    setDrawerDrag(null);
  };
  const ndTap = (fn) => () => {
    if (suppressDrawerClick.current) { suppressDrawerClick.current = false; return; }
    fn();
  };
  const { canInstall, prompt: promptInstall, dismiss: dismissInstall } = usePwaInstall();
  const {
    toasts, theme, toggleTheme,
    fileName, hasUnsavedChanges, externalChangeDetected,
    saveToFile, reloadFromFile, dismissExternalChange,
    isDriveSyncing, isDriveConnecting, googleDriveOn,
    driveNeedsReconnect, reconnectDrive, googleDriveEmail, googleDriveLastSync,
    driveDirty, saveToDriveNow,
    entrySheet, openEntrySheet, closeEntrySheet,
  } = useBudget();

  const [driveModalDismissed, setDriveModalDismissed] = useState(false);
  useEffect(() => {
    if (!driveNeedsReconnect) setDriveModalDismissed(false);
  }, [driveNeedsReconnect]);

  // Ctrl+S → save to file
  useEffect(() => {
    function handleKeyDown(e) {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        saveToFile();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [saveToFile]);

  // Preload all tab chunks after mount so switching feels instant
  useEffect(() => {
    const ric = window.requestIdleCallback ?? ((cb) => setTimeout(cb, 200));
    const cic = window.cancelIdleCallback   ?? clearTimeout;
    const id = ric(() => {
      import('./components/BudgetPlanner/BudgetPlanner');
      import('./components/Tracker/Tracker');
      import('./components/Savings/Savings');
      import('./components/Currency/Currency');
      import('./components/Settings/Settings');
    }, { timeout: 3000 });
    return () => cic(id);
  }, []);

  const isDark = theme === 'dark';

  return (
    <div className="app-layout">
      {isPhone && (
        <header className={`mobile-header${scrolled ? ' scrolled' : ''}`}>
          <button className="mh-burger" onClick={() => setMenuOpen(true)} aria-label="Open menu">
            <Menu size={22} />
          </button>
          <span className="mh-brand">Pennywise</span>
          <div className="mh-actions">
            <DriveStatusChip
              on={googleDriveOn}
              connecting={isDriveConnecting}
              syncing={isDriveSyncing}
              needsReconnect={driveNeedsReconnect}
              dirty={driveDirty}
              lastSync={googleDriveLastSync}
              onReconnect={reconnectDrive}
              onSave={saveToDriveNow}
            />
          </div>
        </header>
      )}

      {isPhone && !menuOpen && (
        <div
          className="drawer-edge"
          onPointerDown={beginDrawer('open')}
          onPointerMove={moveDrawer}
          onPointerUp={endDrawer}
          onPointerCancel={endDrawer}
        />
      )}

      {isPhone && (
        <div
          className={`nav-drawer-backdrop${drawerPos > 0 ? ' open' : ''}`}
          style={{
            backgroundColor: `rgba(31,42,39,${(0.40 * drawerPos / DRAWER_W).toFixed(3)})`,
            pointerEvents: menuOpen && !drawerDragging ? 'auto' : 'none',
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setMenuOpen(false); }}
        >
          <nav
            className={`nav-drawer${drawerDragging ? ' dragging' : ''}`}
            aria-label="Navigation"
            style={{ transform: `translateX(${drawerPos - DRAWER_W}px)` }}
            onPointerDown={beginDrawer('close')}
            onPointerMove={moveDrawer}
            onPointerUp={endDrawer}
            onPointerCancel={endDrawer}
          >
            <div className="nd-header">
              <div className="brand-icon"><img src={`${process.env.PUBLIC_URL}/icons/icon.png`} alt="Pennywise" /></div>
              <span>Pennywise</span>
              <button className="btn btn-ghost btn-icon" onClick={() => setMenuOpen(false)} aria-label="Close menu" style={{ marginLeft: 'auto' }}>
                <X size={16} />
              </button>
            </div>
            {TABS.map(({ id, label, Icon }) => (
              <button
                key={id}
                className={`nd-item${tab === id ? ' active' : ''}`}
                onClick={ndTap(() => { setTab(id); setMenuOpen(false); })}
              >
                <Icon size={18} />
                <span>{label}</span>
              </button>
            ))}
            <div className="nd-footer">
              <button className="nd-item" onClick={ndTap(toggleTheme)}>
                {isDark ? <Sun size={18} /> : <Moon size={18} />}
                <span>{isDark ? 'Light mode' : 'Dark mode'}</span>
              </button>
              <DriveStatusChip
                on={googleDriveOn}
                connecting={isDriveConnecting}
                syncing={isDriveSyncing}
                needsReconnect={driveNeedsReconnect}
                dirty={driveDirty}
                lastSync={googleDriveLastSync}
                onReconnect={reconnectDrive}
                onSave={saveToDriveNow}
              />
            </div>
          </nav>
        </div>
      )}

      <header className="topbar">
        <div className="topbar-brand">
          <div className="brand-icon"><img src={`${process.env.PUBLIC_URL}/icons/icon.png`} alt="Pennywise" /></div>
          <span>Pennywise</span>
        </div>
        <nav className="nav-tabs" ref={navRef}>
          {navPill && (
            <span
              className="nav-pill"
              style={{ transform: `translateX(${navPill.left}px)`, width: navPill.width }}
            />
          )}
          {TABS.map(({ id, label, Icon }) => (
            <button key={id} className={`nav-tab${tab === id ? ' active' : ''}`} onClick={() => setTab(id)}>
              <Icon size={14} />
              <span>{label}</span>
            </button>
          ))}
        </nav>
        <div className="topbar-actions">
          <DriveStatusChip
            on={googleDriveOn}
            connecting={isDriveConnecting}
            syncing={isDriveSyncing}
            needsReconnect={driveNeedsReconnect}
            dirty={driveDirty}
            lastSync={googleDriveLastSync}
            onReconnect={reconnectDrive}
            onSave={saveToDriveNow}
          />
          <FileSaveChip
            fileName={fileName}
            hasUnsavedChanges={hasUnsavedChanges}
            onSave={saveToFile}
          />
          <ThemeToggle theme={theme} onToggle={toggleTheme} />
        </div>
      </header>

      {externalChangeDetected && (
        <ExternalChangeBar
          fileName={fileName}
          onReload={reloadFromFile}
          onDismiss={dismissExternalChange}
        />
      )}

      {canInstall && (
        <div className="pwa-install-bar" role="banner">
          <Download size={14} />
          <span>Install Pennywise for offline access and a faster experience.</span>
          <button className="btn btn-sm btn-primary" onClick={promptInstall}>Install app</button>
          <button className="btn btn-ghost btn-sm btn-icon" onClick={dismissInstall} aria-label="Dismiss"><X size={12} /></button>
        </div>
      )}

      <main className="main-content">
        <Suspense fallback={<div className="tab-loading" aria-label="Loading…" />}>
          {tab === 'dashboard'  && <ErrorBoundary tabName="Dashboard"><Dashboard /></ErrorBoundary>}
          {tab === 'budget'     && <ErrorBoundary tabName="Budget Planner"><BudgetPlanner /></ErrorBoundary>}
          {tab === 'tracker'    && <ErrorBoundary tabName="Tracker"><Tracker /></ErrorBoundary>}
          {tab === 'savings'    && <ErrorBoundary tabName="Savings"><Savings /></ErrorBoundary>}
          {tab === 'currencies' && <ErrorBoundary tabName="Currencies"><Currency /></ErrorBoundary>}
          {tab === 'settings'   && <ErrorBoundary tabName="Settings"><Settings /></ErrorBoundary>}
        </Suspense>
      </main>

      <ToastContainer toasts={toasts} />

      {driveNeedsReconnect && !driveModalDismissed && (
        <Modal
          title="Google Drive — reconnect required"
          onClose={() => setDriveModalDismissed(true)}
          footer={
            <>
              <button className="btn" onClick={() => setDriveModalDismissed(true)}>Later</button>
              <button
                className="btn btn-primary"
                disabled={isDriveConnecting}
                onClick={() => { reconnectDrive(); setDriveModalDismissed(true); }}
              >
                <CloudOff size={14} />
                {isDriveConnecting ? 'Connecting…' : 'Reconnect'}
              </button>
            </>
          }
        >
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, margin: 0 }}>
            Your Google Drive session for <strong>{googleDriveEmail}</strong> has expired.
            Reconnect now to keep your budget synced, or tap <em>Later</em> to continue without sync.
          </p>
        </Modal>
      )}

      {isPhone && (
        <>
          <BottomNav tab={tab} onTab={setTab} onAdd={() => openEntrySheet(null)} compact={scrolled} />
          <MobileEntrySheet open={entrySheet.open} editTxn={entrySheet.editTxn} onClose={closeEntrySheet} />
        </>
      )}
    </div>
  );
}

export default function App() {
  return (
    <BudgetProvider>
      <AppInner />
    </BudgetProvider>
  );
}
